import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { Organization } from '../../services';
import Accordion from '../shared/Accordion';
import Tooltip from '../shared/Tooltip';
import TableScrollTopBar from '../shared/TableScrollTopBar';
import DualCalendarDropdown from '../../../../shared/components/ui/DualCalendarDropdown';
import LoadingSpinner from '../../../../shared/components/ui/LoadingSpinner';
import { organizationsService } from '../../services';

interface OrganizationsTableProps {
  searchQuery: string;
  refreshTrigger?: number;
  onEdit?: (entity: Organization) => void;
}

interface TableColumn {
  name: string;
  type: string;
  nullable: boolean;
  primaryKey?: boolean;
  foreignKey?: string;
  width?: number;
  minWidth?: number;
  maxWidth?: number;
}

interface TableRow {
  [key: string]: string | number | boolean | null;
}

interface PaginationState {
  currentPage: number;
  totalPages: number;
  recordsPerPage: number;
  totalRecords: number;
}

interface FilterState {
  active: 'all' | 'true' | 'false';
  require_api_whitelist: 'all' | 'true' | 'false';
  createdStartDate: Date | null;
  createdEndDate: Date | null;
  updatedStartDate: Date | null;
  updatedEndDate: Date | null;
}

const OrganizationsTable: React.FC<OrganizationsTableProps> = ({ 
  searchQuery, 
  refreshTrigger, 
  onEdit 
}) => {
  const [data, setData] = useState<Organization[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [pagination, setPagination] = useState<PaginationState>({
    currentPage: 1,
    totalPages: 1,
    recordsPerPage: 100,
    totalRecords: 0
  });
  const [sortConfig, setSortConfig] = useState<{
    key: string | null;
    direction: 'asc' | 'desc';
  }>({ key: null, direction: 'asc' });
  const [filters, setFilters] = useState<FilterState>({
    active: 'all',
    require_api_whitelist: 'all',
    createdStartDate: null,
    createdEndDate: null,
    updatedStartDate: null,
    updatedEndDate: null
  });
  const [activeFilterType, setActiveFilterType] = useState<'created' | 'updated' | null>(null);
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);
  const [calendarPosition, setCalendarPosition] = useState({ top: 0, left: 0 });
  const calendarButtonRef = useRef<HTMLButtonElement>(null);
  const updatedCalendarButtonRef = useRef<HTMLButtonElement>(null);

  const calculateCalendarPosition = (buttonRef: React.RefObject<HTMLButtonElement>) => {
    if (buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setCalendarPosition({
        top: rect.bottom + window.scrollY + 8, // 8px gap
        left: rect.left + window.scrollX
      });
    }
  };

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    try {
      const organizations = await organizationsService.getAll();
      setData(organizations);
    } catch (err) {
      console.error('Error fetching organizations:', err);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    if (refreshTrigger !== undefined) {
      fetchData();
    }
  }, [refreshTrigger, fetchData]);

  useEffect(() => {
    setPagination(prev => ({
      ...prev,
      totalRecords: data.length,
      totalPages: Math.ceil(data.length / prev.recordsPerPage)
    }));
  }, [data]);

  const getTableSchema = (): TableColumn[] => {
    return [
      { name: 'id', type: 'uuid', nullable: false, primaryKey: true, width: 250, minWidth: 200, maxWidth: 300 },
      { name: 'name', type: 'varchar', nullable: false, width: 200, minWidth: 150, maxWidth: 250 },
      { name: 'subdomain', type: 'varchar', nullable: true, width: 150, minWidth: 100, maxWidth: 200 },
      { name: 'logo', type: 'varchar', nullable: true, width: 150, minWidth: 100, maxWidth: 200 },
      { name: 'active', type: 'boolean', nullable: false, width: 80, minWidth: 60, maxWidth: 100 },
      { name: 'allowed_domains', type: 'jsonb', nullable: true, width: 200, minWidth: 150, maxWidth: 250 },
      { name: 'allowed_ips', type: 'jsonb', nullable: true, width: 200, minWidth: 150, maxWidth: 250 },
      { name: 'require_api_whitelist', type: 'boolean', nullable: false, width: 150, minWidth: 120, maxWidth: 180 },
      { name: 'created_at', type: 'timestamp', nullable: false, width: 120, minWidth: 100, maxWidth: 150 },
      { name: 'updated_at', type: 'timestamp', nullable: false, width: 120, minWidth: 100, maxWidth: 150 }
    ];
  };

  const formatTableData = (data: Organization[]): TableRow[] => {
    return data.map(org => ({
      id: org.id,
      name: org.name,
      subdomain: org.subdomain || '',
      logo: org.logo || '',
      active: org.active ? 'Yes' : 'No',
      allowed_domains: org.allowed_domains ? JSON.stringify(org.allowed_domains) : '',
      allowed_ips: org.allowed_ips ? JSON.stringify(org.allowed_ips) : '',
      require_api_whitelist: org.require_api_whitelist ? 'Yes' : 'No',
      created_at: new Date(org.created_at).toLocaleDateString(),
      updated_at: new Date(org.updated_at).toLocaleDateString()
    }));
  };

  const filteredData = useMemo(() => {
    let filtered = [...data];

    // Apply search filter
    if (searchQuery.trim()) {
      filtered = filtered.filter(item =>
        item.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        item.subdomain?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        item.logo?.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    // Apply active filter
    if (filters.active !== 'all') {
      filtered = filtered.filter(item => 
        filters.active === 'true' ? item.active : !item.active
      );
    }

    // Apply require_api_whitelist filter
    if (filters.require_api_whitelist !== 'all') {
      filtered = filtered.filter(item => 
        filters.require_api_whitelist === 'true' ? item.require_api_whitelist : !item.require_api_whitelist
      );
    }

    // Apply date filters
    if (filters.createdStartDate) {
      filtered = filtered.filter(item => 
        new Date(item.created_at) >= filters.createdStartDate!
      );
    }
    if (filters.createdEndDate) {
      filtered = filtered.filter(item => 
        new Date(item.created_at) <= filters.createdEndDate!
      );
    }
    if (filters.updatedStartDate) {
      filtered = filtered.filter(item => 
        new Date(item.updated_at) >= filters.updatedStartDate!
      );
    }
    if (filters.updatedEndDate) {
      filtered = filtered.filter(item => 
        new Date(item.updated_at) <= filters.updatedEndDate!
      );
    }

    return filtered;
  }, [data, searchQuery, filters]);

  const sortData = (data: Organization[], sortConfig: { key: string | null; direction: 'asc' | 'desc' }) => {
    if (!sortConfig.key) return data;

    return [...data].sort((a, b) => {
      const aValue = a[sortConfig.key as keyof Organization];
      const bValue = b[sortConfig.key as keyof Organization];

      if (aValue === null || aValue === undefined) return 1;
      if (bValue === null || bValue === undefined) return -1;

      // Helper function to check if a value is a date string
      const isDateString = (value: any): boolean => {
        if (typeof value !== 'string') return false;
        // Check if it matches common date formats
        return /^\d{4}-\d{2}-\d{2}/.test(value) || // YYYY-MM-DD
               /^\d{2}\/\d{2}\/\d{4}/.test(value) || // DD/MM/YYYY
               /^\d{4}-\d{2}-\d{2}T/.test(value); // ISO format
      };

      // Handle date sorting for any date column
      if (isDateString(aValue) && isDateString(bValue)) {
        const aDate = new Date(aValue as string);
        const bDate = new Date(bValue as string);
        
        // Check if dates are valid
        if (!isNaN(aDate.getTime()) && !isNaN(bDate.getTime())) {
          return sortConfig.direction === 'asc' 
            ? aDate.getTime() - bDate.getTime() 
            : bDate.getTime() - aDate.getTime();
        }
      }

      // Handle string sorting
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        return sortConfig.direction === 'asc' 
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      }

      // Handle number sorting
      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return sortConfig.direction === 'asc' 
          ? aValue - bValue
          : bValue - aValue;
      }

      // Handle boolean sorting
      if (typeof aValue === 'boolean' && typeof bValue === 'boolean') {
        return sortConfig.direction === 'asc' 
          ? (aValue === bValue ? 0 : (aValue ? 1 : -1))
          : (aValue === bValue ? 0 : (aValue ? -1 : 1));
      }

      return 0;
    });
  };

  const handleSort = (key: string) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const handlePageChange = (page: number) => {
    setPagination(prev => ({ ...prev, currentPage: page }));
  };

  const handleRecordsPerPageChange = (recordsPerPage: number) => {
    setPagination(prev => ({
      ...prev,
      recordsPerPage,
      currentPage: 1,
      totalPages: Math.ceil(prev.totalRecords / recordsPerPage)
    }));
  };

  const schema = getTableSchema();
  const sortedData = sortData(filteredData, sortConfig);
  const formattedData = formatTableData(sortedData);
  
  const startIndex = (pagination.currentPage - 1) * pagination.recordsPerPage;
  const endIndex = startIndex + pagination.recordsPerPage;
  const paginatedData = formattedData.slice(startIndex, endIndex);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col space-y-4 overflow-visible">
      {/* Table Schema Accordion */}
      <Accordion
        title="Table Schema"
        defaultOpen={false}
      >
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 border-t border-b border-gray-200">
                <th className="px-3 py-3 text-left text-sm font-bold text-gray-900 border-r border-gray-200 bg-gray-100">Column</th>
                <th className="px-3 py-3 text-left text-sm font-bold text-gray-900 border-r border-gray-200 bg-gray-100">Type</th>
                <th className="px-3 py-3 text-left text-sm font-bold text-gray-900 border-r border-gray-200 bg-gray-100">Nullable</th>
                <th className="px-3 py-3 text-left text-sm font-bold text-gray-900 border-r border-gray-200 bg-gray-100">Primary Key</th>
                <th className="px-3 py-3 text-left text-sm font-bold text-gray-900 bg-gray-100">Foreign Key</th>
              </tr>
            </thead>
            <tbody>
              {schema.map((column, index) => (
                <tr key={index} className="border-b border-gray-200">
                  <td className="px-3 py-2 font-mono text-gray-900">{column.name}</td>
                  <td className="px-3 py-2 text-gray-600">{column.type}</td>
                  <td className="px-3 py-2 text-gray-600">{column.nullable ? 'Yes' : 'No'}</td>
                  <td className="px-3 py-2 text-gray-600">{column.primaryKey ? 'Yes' : 'No'}</td>
                  <td className="px-3 py-2 text-gray-600">{column.foreignKey || '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Accordion>

      {/* Table Data Accordion */}
      <Accordion
        title="Table Data"
        defaultOpen={true}
      >
        <div className="space-y-4 overflow-visible">
          {/* Filters */}
          <div className="flex flex-wrap items-center gap-2 p-4 bg-gray-50 rounded-lg overflow-visible">
            <span className="text-sm font-medium text-gray-700">Filters:</span>
            
            {/* Active Filter */}
            <select
              value={filters.active}
              onChange={(e) => setFilters(prev => ({ ...prev, active: e.target.value as 'all' | 'true' | 'false' }))}
              className="px-3 py-1 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">Active: All</option>
              <option value="true">Active: Yes</option>
              <option value="false">Active: No</option>
            </select>

            {/* Require API Whitelist Filter */}
            <select
              value={filters.require_api_whitelist}
              onChange={(e) => setFilters(prev => ({ ...prev, require_api_whitelist: e.target.value as 'all' | 'true' | 'false' }))}
              className="px-3 py-1 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">API Whitelist: All</option>
              <option value="true">API Whitelist: Yes</option>
              <option value="false">API Whitelist: No</option>
            </select>

            {/* Date Filters */}
            <div className="flex items-center space-x-2 relative">
              <button
                ref={calendarButtonRef}
                onClick={() => {
                  calculateCalendarPosition(calendarButtonRef);
                  setActiveFilterType('created');
                  setIsCalendarOpen(!isCalendarOpen);
                }}
                className={`px-3 py-1 text-sm border rounded-md transition-colors ${
                  filters.createdStartDate || filters.createdEndDate
                    ? 'bg-blue-100 border-blue-300 text-blue-700'
                    : 'border-gray-300 hover:bg-gray-50'
                }`}
              >
                Created: {filters.createdStartDate || filters.createdEndDate ? 'Selected' : 'Select'}
              </button>
              
              <button
                ref={updatedCalendarButtonRef}
                onClick={() => {
                  calculateCalendarPosition(updatedCalendarButtonRef);
                  setActiveFilterType('updated');
                  setIsCalendarOpen(!isCalendarOpen);
                }}
                className={`px-3 py-1 text-sm border rounded-md transition-colors ${
                  filters.updatedStartDate || filters.updatedEndDate
                    ? 'bg-blue-100 border-blue-300 text-blue-700'
                    : 'border-gray-300 hover:bg-gray-50'
                }`}
              >
                Updated: {filters.updatedStartDate || filters.updatedEndDate ? 'Selected' : 'Select'}
              </button>

              {/* Calendar Dropdowns - Rendered via Portal */}
              {isCalendarOpen && createPortal(
                <div 
                  className="fixed z-[9999]"
                  style={{
                    top: `${calendarPosition.top}px`,
                    left: `${calendarPosition.left}px`
                  }}
                >
                  {activeFilterType === 'created' && (
                    <DualCalendarDropdown
                      isOpen={isCalendarOpen}
                      onClose={() => setIsCalendarOpen(false)}
                      onDateRangeSelect={(startDate, endDate) => {
                        setFilters(prev => ({
                          ...prev,
                          createdStartDate: startDate,
                          createdEndDate: endDate
                        }));
                        setIsCalendarOpen(false);
                      }}
                      triggerRef={calendarButtonRef}
                    />
                  )}
                  {activeFilterType === 'updated' && (
                    <DualCalendarDropdown
                      isOpen={isCalendarOpen}
                      onClose={() => setIsCalendarOpen(false)}
                      onDateRangeSelect={(startDate, endDate) => {
                        setFilters(prev => ({
                          ...prev,
                          updatedStartDate: startDate,
                          updatedEndDate: endDate
                        }));
                        setIsCalendarOpen(false);
                      }}
                      triggerRef={updatedCalendarButtonRef}
                    />
                  )}
                </div>,
                document.body
              )}
            </div>

            {/* Clear All Filters */}
            <button
              onClick={() => setFilters({
                active: 'all',
                require_api_whitelist: 'all',
                createdStartDate: null,
                createdEndDate: null,
                updatedStartDate: null,
                updatedEndDate: null
              })}
              className="px-2 py-1 text-xs text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded transition-colors"
            >
              Clear Filters
            </button>
          </div>

          {/* Table */}
          <TableScrollTopBar contentClassName="overflow-y-visible">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 sticky top-0 z-10 border-t border-b border-gray-200">
                <tr>
                  {schema.map((column) => (
                    <th
                      key={column.name}
                      className="px-3 py-3 text-left text-sm font-bold text-gray-900 cursor-pointer hover:bg-gray-100 transition-colors border-r border-gray-200 bg-gray-100"
                      style={{ width: column.width, minWidth: column.minWidth, maxWidth: column.maxWidth }}
                      onClick={() => handleSort(column.name)}
                    >
                      <div className="flex items-center space-x-1">
                        <span>{column.name}</span>
                        {sortConfig.key === column.name && (
                          <span className="text-blue-600">
                            {sortConfig.direction === 'asc' ? '↑' : '↓'}
                          </span>
                        )}
                      </div>
                    </th>
                  ))}
                  <th className="px-3 py-3 text-left text-sm font-bold text-gray-900 w-20 bg-gray-100">Actions</th>
                </tr>
              </thead>
              <tbody>
                {paginatedData.length === 0 ? (
                  <tr>
                    <td colSpan={schema.length + 1} className="px-6 py-12 text-center">
                      <div className="flex flex-col items-center justify-center text-gray-500">
                        <span className="text-4xl mb-2">📭</span>
                        <span className="text-lg font-medium">No data to show</span>
                        <span className="text-sm text-gray-400 mt-1">Try adjusting your filters or check back later</span>
                      </div>
                    </td>
                  </tr>
                ) : (
                  paginatedData.map((row, index) => (
                    <tr key={index} className="border-b border-gray-200 hover:bg-gray-50">
                      {schema.map((column) => (
                        <td
                          key={column.name}
                          className="px-3 py-2 text-gray-900 truncate"
                          style={{ width: column.width, minWidth: column.minWidth, maxWidth: column.maxWidth }}
                        >
                          <Tooltip content={String(row[column.name] || '')}>
                            <span>{String(row[column.name] || '')}</span>
                          </Tooltip>
                        </td>
                      ))}
                      <td className="px-3 py-2">
                        <div className="flex items-center space-x-2">
                          <button
                            onClick={() => onEdit?.(data[startIndex + index])}
                            className="p-1 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded transition-colors"
                            title="Edit"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                            </svg>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </TableScrollTopBar>

          {/* Pagination */}
          <div className="flex items-center justify-between px-4 py-3 bg-white border-t border-gray-200">
            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-2">
                <span className="text-sm text-gray-700">Rows per page:</span>
                <select
                  value={pagination.recordsPerPage}
                  onChange={(e) => handleRecordsPerPageChange(Number(e.target.value))}
                  className="px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value={10}>10</option>
                  <option value={100}>100</option>
                  <option value={1000}>1000</option>
                </select>
              </div>
              <span className="text-sm text-gray-700">
                Showing {startIndex + 1} to {Math.min(endIndex, pagination.totalRecords)} of {pagination.totalRecords} records
              </span>
            </div>
            <div className="flex items-center space-x-2">
              <button
                onClick={() => handlePageChange(pagination.currentPage - 1)}
                disabled={pagination.currentPage === 1 || pagination.totalRecords === 0}
                className="px-3 py-1 text-sm border border-gray-300 rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
              >
                Previous
              </button>
              <span className="px-3 py-1 text-sm text-gray-700">
                Page {pagination.totalRecords === 0 ? 0 : pagination.currentPage} of {pagination.totalPages}
              </span>
              <button
                onClick={() => handlePageChange(pagination.currentPage + 1)}
                disabled={pagination.currentPage === pagination.totalPages || pagination.totalRecords === 0}
                className="px-3 py-1 text-sm border border-gray-300 rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
              >
                Next
              </button>
            </div>
          </div>
        </div>
      </Accordion>
    </div>
  );
};

export default OrganizationsTable;
